import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { MapPlace } from "./models/MapPlace";
import { Store, select } from "@ngrx/store";
import { HttpClient } from "@angular/common/http";
import { first, map } from "rxjs/operators";
import { PolygonPoint } from "./models/PolygonPoint";
import { environment } from "src/environments/environment";
import { MapSector, Sector } from "./models/Sector";
import { PlaceTicket } from "./models/PlaceTicket";
import { Map, MapView } from "./models/Map";
import { setLoading, setSectors, setSelectedSector, setShow, setView } from "./store/map.actions";
import { loadingSelector, mapSelector, sectorsSelector, selectedSectorSelector, showSelector, viewSelector } from "./store/map.selectors";
import { Socket, io } from 'socket.io-client';
import { Cart } from "./models/Cart";
import { ReserveSocketEvent } from "./models/ReserveSocketEvent";
import { MapV2Change } from "./models/MapV2Change";

@Injectable({
  providedIn: 'root'
})
export class MapV2Service {
  private cartStorageKey: string;
  private apiUrl: string;
  private mapSubject: BehaviorSubject<Map | null>;
  private reservesSubject: BehaviorSubject<MapV2Change | null>;
  //private socketIO: Socket;

  constructor(
    private store: Store<any>,
    private httpClient: HttpClient
  ) {
    this.cartStorageKey = 'MapV2_Cart_State';
    this.mapSubject = new BehaviorSubject<Map | null>(null);
    this.reservesSubject = new BehaviorSubject<MapV2Change | null>(null);
    this.apiUrl = environment.apiUrl;
    if (this.apiUrl.slice(-1) != '/') {
      this.apiUrl += '/';
    }
    // this.socketIO = io(this.apiUrl, {
    //   transports: ['websocket']
    // });
  }

  public isShowing(): Observable<boolean> {
    return this.store.pipe(select(showSelector));
  }

  public show(): void {
    this.store.dispatch(setShow({ isShowing: true }));
  }

  public hide(): void {
    this.store.dispatch(setShow({ isShowing: false }));
  }

  public getView(): Observable<MapView> {
    return this.store.pipe(select(viewSelector));
  }

  public setView(view: MapView): void {
    this.store.dispatch(setView({ view }));
  }

  public loadMap(eventId: string): Promise<Map | null> {
    this.setLoading(true);
    return new Promise((resolve, reject) => {
      this.httpClient.get<any>(this.apiUrl + `map/event/${eventId}?loadEventSite=true`)
        .pipe(
          first(),
          map((data) => {
            if (Object.keys(data).length === 0) return null;
            return {
              id: data.id,
              eventId: data.eventId,
              image: data.image,
              imageSectors: data.imageSectors,
              color: data.color,
              places: data.places.map((place: any) => {
                return {
                  row: place.row,
                  number: place.number,
                  mapId: place.mapId,
                  coords: place.coords,
                  shape: place.shape,
                  width: place.width,
                  height: place.height,
                  quantity: place.quantity,
                  available: !place.reservedAt,
                  isReserved: false,
                  selectedTicket: null,
                  tickets: place.tickets.map((ticket: any) => {
                    const dateTicket = ticket.dateTickets[0];
                    const lotCurrent = dateTicket.lotCurrent;
                    const prices = lotCurrent.prices[0];
                    const price = prices.priceVirtual - prices.priceFeeVirtual;
                    const type = String(ticket.type).toLocaleLowerCase() == 'full'
                      ? 'Inteira'
                      : 'Meia';
                    return {
                      id: ticket.id,
                      sectorId: ticket.sectorId,
                      name: ticket.name,
                      lotId: lotCurrent.id,
                      lotName: lotCurrent.name,
                      dateTicketId: dateTicket.id,
                      price: price,
                      fee: prices.priceFeeVirtual,
                      type: type,
                      gender: 'Unisex',
                    } as PlaceTicket
                  }),
                } as MapPlace
              }),
            } as Map
          })
        )
        .subscribe({
          next: (map) => {
            this.setLoading(false);

            if (map) {
              let cart = this.getCart();
              if (!cart || cart.eventId != map.eventId || cart.mapId != map.id) {
                cart = {
                  eventId: map.eventId,
                  mapId: map.id,
                  places: []
                }
              }

              if (cart.places.length > 0) {
                // Atualiza map com os places do cart
                map.places = map.places.map(p => {
                  const cartPlace = cart.places.find(cp => cp.row == p.row && cp.number == p.number);
                  if (cartPlace) {
                    return cartPlace;
                  }
                  return p;
                });
              }

              this.setCart(cart);
              this.setMap(map);
              this.loadReserveListeners(map.id);
            }
            resolve(map);
          },
          error: (error) => {
            this.setLoading(false);
            reject(error);
          }
        });
    });
  }

  public getMap(): Observable<Map | null> {
    return of(this.mapSubject.value);
  }

  public setMap(map: Map): void {
    this.mapSubject.next(map);
  }

  public loadSectors(eventId: string): Promise<Sector[]> {
    this.setLoading(true);
    return new Promise((resolve, reject) => {
      this.httpClient.get<{ sectors: any[] }>(this.apiUrl + `event/ticket?eventId=${eventId}`)
        .pipe(
          first(),
          map(res => {
            return res.sectors.map((sector) => {
              const mapSectors: MapSector[] = [];

              sector.mapSectors.forEach((mapSector: any) => {
                const coords: PolygonPoint[] = [];
                if (String(mapSector.coords).includes(';')) {
                  String(mapSector.coords).split(';')
                    .forEach((coord) => {
                      const [x, y] = coord.split(',');
                      coords.push({
                        x: Number(x),
                        y: Number(y)
                      });
                    });
                    mapSectors.push({
                      id: mapSector.id,
                      coords
                    });
                }
              });

              const tickets = Array.from<any>(sector.tickets)
                .map((ticket) => ({
                  id: ticket.id,
                  name: ticket.name
                }));

              return {
                id: sector.id,
                name: sector.name,
                mapSectors,
                tickets
              } as Sector
            });
          })
        ).subscribe({
          next: (sectors) => {
            this.setSectors(sectors);
            this.setLoading(false);
            resolve(sectors);
          },
          error: (error) => {
            this.setLoading(false);
            reject(error);
          }
        });
    });
  }

  public getSectors(): Observable<Sector[]> {
    return this.store.pipe(select(sectorsSelector));
  }

  public getMapWithSectors(): Observable<Map|null> {
    return this.store.pipe(select(mapSelector))
  }

  public getCurrentSectors(): Promise<Sector[]> {
    return new Promise((resolve) => {
        this.getSectors()
        .pipe(first())
        .subscribe({
          next: sectors => {
            resolve(sectors);
          }
        });
    });
  }

  public setSectors(sectors: Sector[]): void {
    this.store.dispatch(setSectors({ sectors }));
  }

  public getSelectedSector(): Observable<Sector | null> {
    return this.store.pipe(select(selectedSectorSelector));
  }

  public setSelectedSector(selectedSector: Sector): void {
    this.store.dispatch(setSelectedSector({ selectedSector }));
    if (!this.hasPlaces(selectedSector.id)) {
      this.reservesSubject.next({
        isSelected: true,
        sectorId: selectedSector.id
      });
    }
  }

  public hasPlaces(sectorId: string): any {
    if (this.mapSubject.value != null) {
      return !!this.mapSubject.value!.places.find(p => p.tickets.some(t => t.sectorId === sectorId));
    }
  }

  public async setSelectedSectorById(sectorId: string): Promise<void> {
    const sectors = await this.getCurrentSectors();
    if (sectors != null && sectors.length >= 1) {
      const selectedSector = sectors.find(s => s.id == sectorId);
      if (selectedSector) {
        this.setSelectedSector(selectedSector);
      }
    }
  }

  public reserve(place: MapPlace, ticket: PlaceTicket): Promise<MapPlace> {
    this.setLoading(true);
    return new Promise((resolve, reject) => {
      const { row, number, mapId } = place;
      this.httpClient.post<any>(this.apiUrl + 'mapPlace/reserve', { row, number, mapId })
        .pipe(first())
        .subscribe({
          next: () => {
            this.setLoading(false);

            const updatedPlace: MapPlace = {
              ...place,
              isReserved: true,
              selectedTicket: ticket
            };

            const cart = this.getCart();
            cart.places.push(updatedPlace);
            this.setCart(cart);

            // marca o lugar reservado no mapa
            const map = this.mapSubject.value;
            const places = map!.places.map(p => {
              if (p.row == row && p.number == number) {
                return updatedPlace;
              }
              return p;
            });
            this.setMap({ ...map as Map, places });

            this.reservesSubject.next({
              isSelected: true,
              sectorId: ticket.sectorId,
              place: updatedPlace,
              ticket: ticket
            });

            resolve(updatedPlace);
          },
          error: (error) => {
            this.setLoading(false);
            reject(error);
          }
        });
    });
  }

  public updateReserve(mapId: string, places: any[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpClient.put<any>(this.apiUrl + 'mapPlace/reserve', { mapId, places })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  public cancelReserve(place: MapPlace): Promise<void> {
    this.setLoading(true);
    return new Promise((resolve, reject) => {
      const { row, number, mapId } = place;
      this.httpClient.post<any>(this.apiUrl + 'mapPlace/reserve/cancel', { row, number, mapId })
        .pipe(first())
        .subscribe({
          next: () => {
            this.setLoading(false);

            const ticket = place.selectedTicket as PlaceTicket;
            const updatedPlace = {
              ...place,
             isReserved: false,
             selectedTicket: null
            }

            const cart = this.getCart();
            cart.places = cart.places.filter(p => p.row != row || p.number != number);
            this.setCart(cart);

            // desmarca o lugar reservado no mapa
            const map = this.mapSubject.value;
            const places = map!.places.map(p => {
              if (p.row == row && p.number == number) {
                return updatedPlace;
              }
              return p;
            });
            this.setMap({ ...map as Map, places });

            this.reservesSubject.next({
              isSelected: false,
              sectorId: ticket.sectorId,
              place: updatedPlace,
              ticket: ticket
            });

            resolve();
          },
          error: (error) => {
            this.setLoading(false);
            reject(error);
          }
        });
    })
  }

  public listenReserves(): Observable<MapV2Change | null> {
    return this.reservesSubject.asObservable();
  }

  public isLoading(): Observable<boolean> {
    return this.store.pipe(select(loadingSelector));
  }

  public setLoading(isLoading: boolean): void {
    this.store.dispatch(setLoading({ isLoading }));
  }

  public getCart(): Cart {
    return JSON.parse(localStorage.getItem(this.cartStorageKey) as string) as Cart;
  }

  public setCart(cart: Cart): void {
    localStorage.setItem(this.cartStorageKey, JSON.stringify(cart));
  }

  public removeCart() {
    localStorage.removeItem(this.cartStorageKey);
  }

  private loadReserveListeners(mapId: string): void {
    return;
    // const eventName = 'mapv2:' + mapId;
    // // Verificar se já está ouvindo o evento
    // if (!this.socketIO.hasListeners(eventName)) {
    //   this.socketIO.on(eventName, (event: ReserveSocketEvent) => {
    //     const map = this.mapSubject.value;
    //     const places = map!.places.map(place => {
    //       if (place.row == event.row && place.number == event.number) {
    //         const available = event.reservedAt == null ? true : false;
    //         return { ...place, available };
    //       }
    //       return place;
    //     });
    //     this.setMap({ ...map as Map, places });
    //   });
    // }
  }
}
